import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { 
  Shield, 
  Users, 
  CreditCard, 
  BarChart3,
  Loader2,
  Lock,
  UserX,
  UserCheck,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Crown,
  Calendar,
  Mail,
  KeyRound,
  ArrowLeft,
  Eye,
  Building2,
  Globe,
  Bell,
  History,
  User,
  Banknote,
  Target,
  Receipt,
  Link2,
  Ticket,
  Plus,
  Percent,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  PiggyBank,
  LogOut,
  Gift,
  CalendarPlus,
  FileText,
  Sparkles,
  Send,
  PenLine,
  ImagePlus,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const ArticleEditor = lazy(() => import("../components/ArticleEditor"));
const ArticlePreviewContent = lazy(() => import("../components/ArticlePreviewContent"));
const AdminArticlesSection = lazy(() => import("../components/AdminArticlesSection"));
const AdminAffiliatesSection = lazy(() => import("../components/AdminAffiliatesSection"));
const AdminPrivacySection = lazy(() => import("../components/AdminPrivacySection"));
const AdminAdvisorsSection = lazy(() => import("../components/AdminAdvisorsSection"));
const AdminSecurityCompliance = lazy(() => import("../components/AdminSecurityCompliance"));

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  trialing: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  cancelled: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  suspended: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  banned: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  pending: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400",
  success: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  comp: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

const PLAN_COLORS = {
  student: "bg-blue-100 text-blue-700",
  power: "bg-amber-100 text-amber-700",
  senior: "bg-emerald-100 text-emerald-700",
  free: "bg-gray-100 text-gray-700",
};

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("tally_admin_token") || "");
  
  // MFA State
  const [mfaStep, setMfaStep] = useState("credentials");
  const [mfaSessionId, setMfaSessionId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaEmail, setMfaEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Admin Dashboard Navigation
  const [activeSection, setActiveSection] = useState("users");

  // Users
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [userFilter, setUserFilter] = useState("all");

  // User Detail View
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  // Subscriptions
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(true);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(0);
  const [subsFilter, setSubsFilter] = useState("all");
  
  // Login Attempts
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(true);
  const [attemptsTotal, setAttemptsTotal] = useState(0);

  // Action loading
  const [actionLoading, setActionLoading] = useState(false);

  // Coupons
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(true);
  const [couponStats, setCouponStats] = useState(null);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    message: "",
    link_url: "",
    link_text: "",
    bg_color: "#1a1a2e",
    text_color: "#ffffff"
  });

  const [newCoupon, setNewCoupon] = useState({
    name: "",
    code: "",
    discount_type: "percent_off",
    discount_value: "",
    duration: "repeating_1",
    max_redemptions: "",
    expires_at: "",
    applies_to: null
  });

  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` }
  });

  // Check if already authenticated
  useEffect(() => {
    if (token) {
      api.get("/api/admin/stats")
        .then(() => setIsAuthenticated(true))
        .catch(() => {
          localStorage.removeItem("tally_admin_token");
          setToken("");
          setIsAuthenticated(false);
        });
    }
  }, []);

  // Step 1: Submit email/password to get MFA code sent
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, { email, password });
      setMfaSessionId(response.data.session_id);
      setMfaEmail(response.data.email_sent_to);
      setMfaStep("verify");
      toast.success(`Verification code sent to ${response.data.email_sent_to}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid admin credentials";
      toast.error(message);
    } finally {
      setLoggingIn(false);
    }
  };

  // Step 2: Verify MFA code
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/verify-mfa`, {
        session_id: mfaSessionId,
        code: mfaCode
      });
      const newToken = response.data.access_token;
      localStorage.setItem("tally_admin_token", newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      setMfaStep("credentials");
      setEmail("");
      setPassword("");
      setMfaCode("");
      toast.success("Admin access granted");
    } catch (error) {
      const message = error.response?.data?.detail || "Verification failed";
      toast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  // Resend MFA code
  const handleResendCode = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/admin/resend-mfa?session_id=${mfaSessionId}`);
      toast.success("New verification code sent");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to resend code";
      toast.error(message);
      if (error.response?.status === 400) {
        setMfaStep("credentials");
        setMfaSessionId("");
        setMfaCode("");
      }
    } finally {
      setResending(false);
    }
  };

  const handleBackToCredentials = () => {
    setMfaStep("credentials");
    setMfaSessionId("");
    setMfaCode("");
  };

  const handleLogout = () => {
    localStorage.removeItem("tally_admin_token");
    setToken("");
    setIsAuthenticated(false);
    setMfaStep("credentials");
    setEmail("");
    setPassword("");
    setMfaCode("");
    toast.info("Logged out");
  };

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setStatsLoading(true);
    try {
      const response = await api.get("/api/admin/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [token]);
  
  const fetchLoginAttempts = useCallback(async () => {
    if (!token) return;
    setAttemptsLoading(true);
    try {
      const response = await api.get("/api/admin/login-attempts?limit=50");
      setLoginAttempts(response.data.attempts || []);
      setAttemptsTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch login attempts:", error);
    } finally {
      setAttemptsLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setUsersLoading(true);
    try {
      const params = { skip: usersPage * 20, limit: 20 };
      if (userFilter !== "all") params.plan = userFilter;
      const response = await api.get("/api/admin/users", { params });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setUsersLoading(false);
    }
  }, [token, usersPage, userFilter]);

  const fetchUserDetail = useCallback(async (userId) => {
    if (!token || !userId) return;
    setUserDetailLoading(true);
    try {
      const response = await api.get(`/api/admin/users/${userId}`);
      setUserDetail(response.data);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      toast.error("Failed to load user details");
    } finally {
      setUserDetailLoading(false);
    }
  }, [token]);

  const fetchSubscriptions = useCallback(async () => {
    if (!token) return;
    setSubsLoading(true);
    try {
      const params = { skip: subsPage * 20, limit: 20 };
      if (subsFilter !== "all") params.status = subsFilter;
      const response = await api.get("/api/admin/subscriptions", { params });
      setSubscriptions(response.data.subscriptions);
      setSubsTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
    } finally {
      setSubsLoading(false);
    }
  }, [token, subsPage, subsFilter]);

  const fetchCoupons = useCallback(async () => {
    if (!token) return;
    setCouponsLoading(true);
    try {
      const couponsRes = await api.get("/api/coupons");
      setCoupons(couponsRes.data);
    } catch (error) {
      console.error("Failed to fetch coupons:", error);
    }
    try {
      const statsRes = await api.get("/api/coupons/stats");
      setCouponStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch coupon stats:", error);
    }
    setCouponsLoading(false);
  }, [token]);

  const fetchAnnouncements = useCallback(async () => {
    if (!token) return;
    setAnnouncementsLoading(true);
    try {
      const res = await api.get("/api/admin/announcements");
      setAnnouncements(res.data);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setAnnouncementsLoading(false);
    }
  }, [token]);

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.message.trim()) {
      toast.error("Message is required");
      return;
    }
    setSavingAnnouncement(true);
    try {
      await api.post("/api/admin/announcements", newAnnouncement);
      toast.success("Announcement created and activated");
      setShowCreateAnnouncement(false);
      setNewAnnouncement({ message: "", link_url: "", link_text: "", bg_color: "#1a1a2e", text_color: "#ffffff" });
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create announcement");
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const handleAnnouncementAction = async (id, action) => {
    try {
      if (action === "activate") {
        await api.patch(`/api/admin/announcements/${id}/activate`);
        toast.success("Announcement activated");
      } else if (action === "deactivate") {
        await api.patch(`/api/admin/announcements/${id}/deactivate`);
        toast.success("Announcement deactivated");
      } else if (action === "delete") {
        await api.delete(`/api/admin/announcements/${id}`);
        toast.success("Announcement deleted");
      }
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchUsers();
      fetchSubscriptions();
    }
  }, [isAuthenticated, fetchStats, fetchUsers, fetchSubscriptions]);

  // When selecting a user, fetch their details
  useEffect(() => {
    if (selectedUserId) {
      fetchUserDetail(selectedUserId);
    }
  }, [selectedUserId, fetchUserDetail]);

  const handleUserAction = async (userId, action, params = {}) => {
    setActionLoading(true);
    try {
      if (action === "delete") {
        await api.delete(`/api/admin/users/${userId}`);
        toast.success("User deleted");
        setSelectedUserId(null);
        setUserDetail(null);
      } else if (action === "status") {
        await api.put(`/api/admin/users/${userId}/status?status=${params.status}`);
        toast.success(`User ${params.status}`);
        fetchUserDetail(userId);
      } else if (action === "plan") {
        await api.put(`/api/admin/users/${userId}/plan?plan=${params.plan}`);
        toast.success(`Plan updated to ${params.plan}`);
        fetchUserDetail(userId);
      }
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubscriptionAction = async (subId, action, params = {}) => {
    setActionLoading(true);
    try {
      if (action === "cancel") {
        await api.put(`/api/admin/subscriptions/${subId}/cancel`);
        toast.success("Subscription cancelled");
      } else if (action === "activate") {
        await api.put(`/api/admin/subscriptions/${subId}/activate`);
        toast.success("Subscription activated");
      } else if (action === "extend") {
        await api.put(`/api/admin/subscriptions/${subId}/extend-trial?days=${params.days || 7}`);
        toast.success(`Trial extended by ${params.days || 7} days`);
      }
      fetchSubscriptions();
      fetchStats();
      if (selectedUserId) fetchUserDetail(selectedUserId);
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Coupon handlers
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setCreatingCoupon(true);
    try {
      const payload = {
        name: newCoupon.name,
        code: newCoupon.code.toUpperCase(),
        discount_type: newCoupon.discount_type,
        discount_value: parseFloat(newCoupon.discount_value),
        duration: newCoupon.duration,
        max_redemptions: newCoupon.max_redemptions ? parseInt(newCoupon.max_redemptions) : null,
        expires_at: newCoupon.expires_at || null,
        applies_to: newCoupon.applies_to
      };
      await api.post("/api/coupons/create", payload);
      toast.success("Coupon created successfully");
      setShowCreateCoupon(false);
      setNewCoupon({
        name: "",
        code: "",
        discount_type: "percent_off",
        discount_value: "",
        duration: "repeating_1",
        max_redemptions: "",
        expires_at: "",
        applies_to: null
      });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    } finally {
      setCreatingCoupon(false);
    }
  };

  const handleCouponAction = async (couponId, action) => {
    setActionLoading(true);
    try {
      if (action === "deactivate") {
        await api.patch(`/api/coupons/${couponId}/deactivate`);
        toast.success("Coupon deactivated");
      } else if (action === "activate") {
        await api.patch(`/api/coupons/${couponId}/activate`);
        toast.success("Coupon activated");
      } else if (action === "delete") {
        await api.delete(`/api/coupons/${couponId}`);
        toast.success("Coupon deleted");
      }
      fetchCoupons();
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const getDurationLabel = (duration) => {
    const labels = {
      once: "One-time",
      repeating_1: "1 Month",
      repeating_6: "6 Months",
      repeating_12: "1 Year",
      forever: "Forever (Comp)"
    };
    return labels[duration] || duration;
  };

  // Subscription management state
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subAction, setSubAction] = useState(null); // { type: 'paid' | 'comp' | 'trial' | 'free', userId, email }
  const [subDuration, setSubDuration] = useState("1_month");
  const [subCustomDays, setSubCustomDays] = useState("");
  const [subReason, setSubReason] = useState("");

  const handleSetSubscriptionStatus = async (userId, status, options = {}) => {
    setActionLoading(true);
    try {
      const payload = { 
        status,
        duration: options.duration,
        custom_days: options.customDays ? parseInt(options.customDays) : null,
        reason: options.reason || null
      };
      await api.put(`/api/admin/users/${userId}/subscription-status`, payload);
      toast.success(options.successMessage || `User subscription updated to ${status.toUpperCase()}`);
      // Reset modal state
      setSubModalOpen(false);
      setSubAction(null);
      setSubDuration("1_month");
      setSubCustomDays("");
      setSubReason("");
      // Refresh user detail
      if (selectedUserId) {
        fetchUserDetail(selectedUserId);
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subscription status");
    } finally {
      setActionLoading(false);
    }
  };

  const openSubscriptionModal = (type, userId, email) => {
    setSubAction({ type, userId, email });
    setSubModalOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Admin Access</CardTitle>
            <p className="text-muted-foreground text-sm mt-2">
              {mfaStep === "credentials" 
                ? "Enter admin credentials to continue" 
                : "Enter the verification code sent to your email"
              }
            </p>
          </CardHeader>
          <CardContent>
            {mfaStep === "credentials" ? (
              <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Admin Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      placeholder="admin@example.com"
                      data-testid="admin-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      placeholder="Enter password"
                      data-testid="admin-password"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loggingIn || !email || !password}
                  data-testid="admin-login-btn"
                >
                  {loggingIn ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending verification code...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Continue with Email Verification
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleMfaVerify} className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div className="text-sm">
                    <p className="font-medium">Code sent to</p>
                    <p className="text-muted-foreground">{mfaEmail}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Verification Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                      className="pl-10 text-center text-2xl tracking-[0.5em] font-mono"
                      placeholder="000000"
                      autoFocus
                      data-testid="admin-mfa-code"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={verifying || mfaCode.length !== 6}
                  data-testid="admin-verify-btn"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Verify & Access Admin
                    </>
                  )}
                </Button>
                <div className="flex items-center justify-between pt-2">
                  <Button type="button" variant="ghost" size="sm" onClick={handleBackToCredentials} className="text-muted-foreground">
                    <ArrowLeft className="mr-1 h-3 w-3" />
                    Back
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={handleResendCode} disabled={resending} className="text-muted-foreground">
                    {resending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
                    Resend code
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // User Detail Panel Component
  const UserDetailPanel = () => {
    if (!selectedUserId) return null;
    
    if (userDetailLoading) {
      return (
        <Card className="col-span-2">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      );
    }

    if (!userDetail) return null;

    const { user, plaid, current_subscription, billing_history, linked_accounts, login_history, stats } = userDetail;

    return (
      <Card className="col-span-2" data-testid="user-detail-panel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setSelectedUserId(null); setUserDetail(null); }}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <CardTitle className="text-xl">User Details</CardTitle>
                <CardDescription>{user.email}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={STATUS_COLORS[user.account_status] || STATUS_COLORS.active}>
                {user.account_status}
              </Badge>
              <Badge className={PLAN_COLORS[user.age_tier] || PLAN_COLORS.free}>
                {user.age_tier}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Profile Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile Information
              </h3>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">{user.name || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{user.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date of Birth</span>
                  <span className="font-medium">{user.date_of_birth ? formatShortDate(user.date_of_birth) : "Not provided"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Currency</span>
                  <span className="font-medium">{user.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Joined</span>
                  <span className="font-medium">{formatShortDate(user.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Login</span>
                  <span className="font-medium">{user.last_login ? formatDate(user.last_login) : "Never"}</span>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Usage Statistics
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{stats.accounts}</p>
                  <p className="text-xs text-muted-foreground">Accounts</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{stats.transactions.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Transactions</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{stats.bills}</p>
                  <p className="text-xs text-muted-foreground">Bills</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{stats.goals}</p>
                  <p className="text-xs text-muted-foreground">Goals</p>
                </div>
              </div>
            </div>
          </div>

          {/* Plaid Connection Status */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Bank Connection (Plaid)
            </h3>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm">Connection Status</span>
                <Badge className={plaid.connected ? STATUS_COLORS.active : STATUS_COLORS.cancelled}>
                  {plaid.connected ? "Connected" : "Not Connected"}
                </Badge>
              </div>
              {plaid.connected && (
                <>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Linked Items</span>
                    <span>{plaid.items_count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Last Sync</span>
                    <span>{formatDate(plaid.last_sync)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Linked Accounts (no balances) */}
            {linked_accounts.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Linked Accounts (no balances shown)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {linked_accounts.map((acc, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-muted/30 rounded text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{acc.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {acc.institution} •••{acc.mask}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Current Subscription */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Crown className="h-4 w-4" />
              Current Subscription
            </h3>
            {current_subscription ? (
              <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <Badge className={PLAN_COLORS[current_subscription.plan] || PLAN_COLORS.free}>
                    {current_subscription.plan}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={STATUS_COLORS[current_subscription.status] || STATUS_COLORS.pending}>
                    {current_subscription.status}
                    {current_subscription.admin_granted && ' (Admin)'}
                  </Badge>
                </div>
                {current_subscription.amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-medium">${current_subscription.amount} CAD/mo</span>
                  </div>
                )}
                {current_subscription.trial_ends_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trial Ends</span>
                    <span>{formatShortDate(current_subscription.trial_ends_at)}</span>
                  </div>
                )}
                {current_subscription.expires_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expires</span>
                    <span className="text-amber-600">{formatShortDate(current_subscription.expires_at)}</span>
                  </div>
                )}
                {current_subscription.comp_type && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Comp Type</span>
                    <span className="text-purple-600">{current_subscription.comp_type.replace(/_/g, ' ')}</span>
                  </div>
                )}
                {current_subscription.admin_granted_by && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Granted By</span>
                    <span className="text-xs">{current_subscription.admin_granted_by}</span>
                  </div>
                )}
                {current_subscription.admin_reason && (
                  <div className="flex flex-col gap-1 pt-2 border-t border-border/50">
                    <span className="text-muted-foreground text-xs">Note</span>
                    <span className="text-xs italic">{current_subscription.admin_reason}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Started</span>
                  <span>{formatShortDate(current_subscription.created_at)}</span>
                </div>
                {current_subscription.stripe_subscription_id && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Stripe ID</span>
                    <code className="text-xs bg-muted px-1 rounded">{current_subscription.stripe_subscription_id}</code>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-muted/50 rounded-lg p-4 text-center text-muted-foreground">
                No active subscription
              </div>
            )}
            
            {/* Subscription Management */}
            <div className="pt-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-3">Subscription Controls</p>
              <div className="grid grid-cols-2 gap-2">
                {/* Comp Access Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => openSubscriptionModal('comp', userDetail.user.id, userDetail.user.email)}
                  data-testid={`comp-${userDetail.user.id}`}
                >
                  <Gift className="h-4 w-4 text-purple-500" />
                  Comp Access
                </Button>
                
                {/* Extend Trial Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => openSubscriptionModal('trial', userDetail.user.id, userDetail.user.email)}
                  data-testid={`extend-trial-${userDetail.user.id}`}
                >
                  <CalendarPlus className="h-4 w-4 text-blue-500" />
                  Extend Trial
                </Button>
                
                {/* Set Paid Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => openSubscriptionModal('paid', userDetail.user.id, userDetail.user.email)}
                  data-testid={`set-paid-${userDetail.user.id}`}
                >
                  <Crown className="h-4 w-4 text-amber-500" />
                  Set to Paid
                </Button>
                
                {/* Set Free Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => openSubscriptionModal('free', userDetail.user.id, userDetail.user.email)}
                  data-testid={`set-free-${userDetail.user.id}`}
                >
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                  Set to Free
                </Button>
              </div>
            </div>
          </div>

          {/* Billing History */}
          {billing_history.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Billing History ({billing_history.length})
              </h3>
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {billing_history.map((sub, i) => (
                  <div key={i} className="p-3 flex items-center justify-between text-sm">
                    <div>
                      <span className="font-medium">{sub.plan}</span>
                      <span className="text-muted-foreground ml-2">${sub.amount}/mo</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={STATUS_COLORS[sub.status]}>
                        {sub.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatShortDate(sub.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Login History */}
          {login_history.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <History className="h-4 w-4" />
                Login History (Last 20)
              </h3>
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {login_history.map((login, i) => (
                  <div key={i} className="p-3 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      {login.status === "success" ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium">{formatDate(login.timestamp)}</p>
                        <p className="text-xs text-muted-foreground">{login.ip_address}</p>
                      </div>
                    </div>
                    <Badge className={STATUS_COLORS[login.status]}>
                      {login.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Actions */}
          <div className="flex items-center gap-2 pt-4 border-t">
            {user.account_status !== "suspended" ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleUserAction(user.id, "status", { status: "suspended" })}
                disabled={actionLoading}
              >
                <UserX className="w-4 h-4 mr-1" />
                Suspend User
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleUserAction(user.id, "status", { status: "active" })}
                disabled={actionLoading}
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Activate User
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete User
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete User?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete {user.email} and all their data. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => handleUserAction(user.id, "delete")}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    Delete User
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Admin Dashboard
  const navItems = [
    { id: "users", label: "Users", icon: Users, count: stats?.total_users || 0 },
    { id: "subscriptions", label: "Subscriptions", icon: CreditCard, count: stats?.subscriptions?.active || 0 },
    { id: "articles", label: "Articles", icon: FileText },
    { id: "affiliates", label: "Affiliates", icon: Link2 },
    { id: "coupons", label: "Coupons", icon: Ticket, onClick: fetchCoupons },
    { id: "announcements", label: "Announcements", icon: Bell, onClick: fetchAnnouncements },
    { id: "privacy", label: "Privacy Gateway", icon: Shield, },
    { id: "advisors", label: "Advisors", icon: Eye, },
    { id: "compliance", label: "Security & Compliance", icon: Shield, },
    { id: "security", label: "Login Log", icon: Shield, onClick: fetchLoginAttempts },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Header Bar */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20">
                <Shield className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight">Tally Admin</h1>
                <p className="text-xs text-muted-foreground">Management Console</p>
              </div>
            </div>
          </div>
          
          {/* Quick Stats in Header */}
          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                {stats?.active_users || 0} Active Today
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              MRR: <span className="font-semibold text-foreground">${((stats?.subscriptions?.paying || 0) * 14.99).toFixed(0)}</span>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2" data-testid="admin-logout">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="hidden md:flex w-64 flex-col border-r bg-muted/30 min-h-[calc(100vh-4rem)]">
          <nav className="flex-1 p-4 space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-3">
              Management
            </p>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setSelectedUserId(null);
                  setUserDetail(null);
                  if (item.onClick) item.onClick();
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeSection === item.id
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1 text-left">{item.label}</span>
                {item.count !== undefined && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeSection === item.id
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {item.count}
                  </span>
                )}
              </button>
            ))}
            
            <div className="pt-4 mt-4 border-t border-border/50">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-3">
                Analytics
              </p>
              <div className="px-3 py-3 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200/50 dark:border-amber-800/50">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-900 dark:text-amber-100">Quick Stats</span>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-amber-700 dark:text-amber-300">Total Users</span>
                    <span className="font-semibold text-amber-900 dark:text-amber-100">{stats?.total_users || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700 dark:text-amber-300">Active Subs</span>
                    <span className="font-semibold text-amber-900 dark:text-amber-100">{stats?.subscriptions?.active || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700 dark:text-amber-300">Trial Users</span>
                    <span className="font-semibold text-amber-900 dark:text-amber-100">{stats?.subscriptions?.trialing || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700 dark:text-amber-300">Cancelled</span>
                    <span className="font-semibold text-amber-900 dark:text-amber-100">{stats?.subscriptions?.cancelled || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </aside>

        {/* Mobile Navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur">
          <nav className="flex justify-around p-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setSelectedUserId(null);
                  setUserDetail(null);
                  if (item.onClick) item.onClick();
                }}
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeSection === item.id
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              >
                <item.icon className={`w-5 h-5 ${activeSection === item.id ? "text-primary" : ""}`} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 p-6 pb-24 md:pb-6">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold tracking-tight">
                {navItems.find(n => n.id === activeSection)?.label}
              </h2>
              <p className="text-muted-foreground text-sm mt-1">
                {activeSection === "users" && "Manage user accounts and access"}
                {activeSection === "subscriptions" && "View and manage billing"}
                {activeSection === "coupons" && "Create and manage discount codes"}
                {activeSection === "articles" && "Create, edit, and publish SEO articles"}
                {activeSection === "affiliates" && "Manage affiliate partners and commissions"}
                {activeSection === "announcements" && "Manage announcement bar on public pages"}
                {activeSection === "privacy" && "Privacy Gateway — audit logs, consent, and data access controls"}
                {activeSection === "advisors" && "Advisor relationships and consent management"}
                {activeSection === "compliance" && "Security operations, incidents, risks, and compliance"}
                {activeSection === "security" && "Monitor login activity"}
              </p>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* User Detail Panel */}
              {selectedUserId && <UserDetailPanel />}

              {/* Main Content */}
              <div className={selectedUserId ? "col-span-1 lg:col-span-1" : "col-span-3"}>
                {/* Users Section */}
                {activeSection === "users" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Select value={userFilter} onValueChange={(v) => { setUserFilter(v); setUsersPage(0); }}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Filter by plan" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Plans</SelectItem>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="power">Power</SelectItem>
                            <SelectItem value="senior">Senior</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="icon" onClick={fetchUsers}>
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">{usersTotal} users</p>
                    </div>

                    <Card>
                      <CardContent className="p-0">
                        {usersLoading ? (
                          <div className="p-6 space-y-3">
                            <Skeleton className="h-12" />
                            <Skeleton className="h-12" />
                            <Skeleton className="h-12" />
                          </div>
                        ) : users.length === 0 ? (
                          <div className="p-6 text-center text-muted-foreground">
                            No users found
                          </div>
                        ) : (
                          <div className="divide-y">
                            {users.map((user) => (
                              <div 
                                key={user.id} 
                                className={`p-4 flex items-center justify-between hover:bg-muted/50 cursor-pointer ${selectedUserId === user.id ? "bg-primary/5 border-l-2 border-primary" : ""}`}
                                onClick={() => setSelectedUserId(user.id)}
                              >
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                                    {user.email_masked.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium">{user.email_masked}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge variant="secondary" className={PLAN_COLORS[user.age_tier] || PLAN_COLORS.free}>
                                        {user.age_tier}
                                      </Badge>
                                      <Badge variant="secondary" className={STATUS_COLORS[user.account_status] || STATUS_COLORS.active}>
                                        {user.account_status || "active"}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        {user.accounts_count} accounts
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm" className="gap-1">
                                  <Eye className="w-4 h-4" />
                                  View
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Pagination */}
                    {usersTotal > 20 && (
                      <div className="flex items-center justify-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setUsersPage(p => Math.max(0, p - 1))}
                          disabled={usersPage === 0}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          Page {usersPage + 1} of {Math.ceil(usersTotal / 20)}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setUsersPage(p => p + 1)}
                          disabled={(usersPage + 1) * 20 >= usersTotal}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Subscriptions Section */}
                {activeSection === "subscriptions" && (
                  <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Select value={subsFilter} onValueChange={(v) => { setSubsFilter(v); setSubsPage(0); }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="trialing">Trialing</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={fetchSubscriptions}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">{subsTotal} subscriptions</p>
                </div>

                <Card>
                  <CardContent className="p-0">
                    {subsLoading ? (
                      <div className="p-6 space-y-3">
                        <Skeleton className="h-12" />
                        <Skeleton className="h-12" />
                        <Skeleton className="h-12" />
                      </div>
                    ) : subscriptions.length === 0 ? (
                      <div className="p-6 text-center text-muted-foreground">
                        No subscriptions found
                      </div>
                    ) : (
                      <div className="divide-y">
                        {subscriptions.map((sub) => (
                          <div key={sub.id} className="p-4 flex items-center justify-between hover:bg-muted/50">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <Crown className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium">{sub.user_email_masked}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="secondary" className={STATUS_COLORS[sub.status] || STATUS_COLORS.pending}>
                                    {sub.status}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    ${sub.amount} CAD/mo
                                  </span>
                                  {sub.status === "trialing" && sub.trial_ends_at && (
                                    <span className="text-xs text-blue-600 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      Trial ends {new Date(sub.trial_ends_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {sub.status === "trialing" && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleSubscriptionAction(sub.id, "extend", { days: 7 })}
                                  disabled={actionLoading}
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  +7 Days
                                </Button>
                              )}
                              {sub.status !== "cancelled" ? (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleSubscriptionAction(sub.id, "cancel")}
                                  disabled={actionLoading}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Cancel
                                </Button>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleSubscriptionAction(sub.id, "activate")}
                                  disabled={actionLoading}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Activate
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Pagination */}
                {subsTotal > 20 && (
                  <div className="flex items-center justify-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSubsPage(p => Math.max(0, p - 1))}
                      disabled={subsPage === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {subsPage + 1} of {Math.ceil(subsTotal / 20)}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSubsPage(p => p + 1)}
                      disabled={(subsPage + 1) * 20 >= subsTotal}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Coupons Section */}
            {activeSection === "coupons" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">Discount Coupons</h3>
                    <Button variant="outline" size="icon" onClick={fetchCoupons}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={async () => {
                      try {
                        const res = await api.get("/api/debug/coupon-check");
                        const d = res.data;
                        toast.info(`Stripe: ${d.stripe_ok ? 'OK' : 'FAIL'} (${d.stripe_key_prefix}) | DB: ${d.db_ok ? 'OK' : 'FAIL'} (${d.db_coupons_count} coupons)`);
                      } catch (err) { toast.error("Debug check failed: " + (err.response?.data?.detail || err.message)); }
                    }} data-testid="coupon-debug-btn">
                      System Check
                    </Button>
                  </div>
                  <Button 
                    onClick={() => setShowCreateCoupon(true)}
                    className="gap-2"
                    data-testid="create-coupon-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Create Coupon
                  </Button>
                </div>

                {/* Coupon Stats */}
                {couponStats && (
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <Ticket className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{couponStats.total_coupons}</p>
                            <p className="text-xs text-muted-foreground">Total Coupons</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{couponStats.active_coupons}</p>
                            <p className="text-xs text-muted-foreground">Active</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{couponStats.total_redemptions}</p>
                            <p className="text-xs text-muted-foreground">Redemptions</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Create Coupon Form */}
                {showCreateCoupon && (
                  <Card className="border-primary/50">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Ticket className="w-5 h-5" />
                        Create New Coupon
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateCoupon} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="coupon-name">Coupon Name (Internal)</Label>
                            <Input
                              id="coupon-name"
                              placeholder="e.g., Holiday Sale 2025"
                              value={newCoupon.name}
                              onChange={(e) => setNewCoupon({...newCoupon, name: e.target.value})}
                              required
                              data-testid="coupon-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="coupon-code">Promotion Code</Label>
                            <Input
                              id="coupon-code"
                              placeholder="e.g., SAVE20"
                              value={newCoupon.code}
                              onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                              required
                              className="uppercase"
                              data-testid="coupon-code-input"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Discount Type</Label>
                            <Select 
                              value={newCoupon.discount_type} 
                              onValueChange={(v) => setNewCoupon({...newCoupon, discount_type: v})}
                            >
                              <SelectTrigger data-testid="coupon-type-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percent_off">
                                  <span className="flex items-center gap-2">
                                    <Percent className="w-4 h-4" /> Percentage Off
                                  </span>
                                </SelectItem>
                                <SelectItem value="amount_off">
                                  <span className="flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" /> Fixed Amount Off
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="discount-value">
                              {newCoupon.discount_type === "percent_off" ? "Percentage (1-100)" : "Amount ($CAD)"}
                            </Label>
                            <Input
                              id="discount-value"
                              type="number"
                              min="1"
                              max={newCoupon.discount_type === "percent_off" ? "100" : "9999"}
                              step={newCoupon.discount_type === "percent_off" ? "1" : "0.01"}
                              placeholder={newCoupon.discount_type === "percent_off" ? "20" : "5.00"}
                              value={newCoupon.discount_value}
                              onChange={(e) => setNewCoupon({...newCoupon, discount_value: e.target.value})}
                              required
                              data-testid="coupon-value-input"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Duration</Label>
                            <Select 
                              value={newCoupon.duration} 
                              onValueChange={(v) => setNewCoupon({...newCoupon, duration: v})}
                            >
                              <SelectTrigger data-testid="coupon-duration-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="once">One-time (first payment only)</SelectItem>
                                <SelectItem value="repeating_1">1 Month</SelectItem>
                                <SelectItem value="repeating_6">6 Months</SelectItem>
                                <SelectItem value="repeating_12">1 Year</SelectItem>
                                <SelectItem value="forever">Forever (Comp)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="max-redemptions">Max Redemptions (optional)</Label>
                            <Input
                              id="max-redemptions"
                              type="number"
                              min="1"
                              placeholder="Unlimited"
                              value={newCoupon.max_redemptions}
                              onChange={(e) => setNewCoupon({...newCoupon, max_redemptions: e.target.value})}
                              data-testid="coupon-max-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="expires-at">Expiration Date (optional)</Label>
                          <Input
                            id="expires-at"
                            type="datetime-local"
                            value={newCoupon.expires_at}
                            onChange={(e) => setNewCoupon({...newCoupon, expires_at: e.target.value})}
                            data-testid="coupon-expires-input"
                          />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => setShowCreateCoupon(false)}
                          >
                            Cancel
                          </Button>
                          <Button 
                            type="submit" 
                            disabled={creatingCoupon}
                            data-testid="create-coupon-submit"
                          >
                            {creatingCoupon ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-2" />
                                Create Coupon
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {/* Coupons List */}
                <Card>
                  <CardContent className="p-0">
                    {couponsLoading ? (
                      <div className="p-6 space-y-3">
                        {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                      </div>
                    ) : coupons.length === 0 ? (
                      <div className="p-12 text-center">
                        <Ticket className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                        <p className="text-muted-foreground">No coupons created yet</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setShowCreateCoupon(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create your first coupon
                        </Button>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {coupons.map((coupon) => (
                          <div key={coupon.id} className="p-4 flex items-center justify-between hover:bg-muted/50">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                coupon.active 
                                  ? "bg-emerald-100 dark:bg-emerald-900/30" 
                                  : "bg-gray-100 dark:bg-gray-800"
                              }`}>
                                {coupon.discount_type === "percent_off" 
                                  ? <Percent className={`w-5 h-5 ${coupon.active ? "text-emerald-600" : "text-gray-500"}`} />
                                  : <DollarSign className={`w-5 h-5 ${coupon.active ? "text-emerald-600" : "text-gray-500"}`} />
                                }
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <code className="font-mono font-bold text-primary">{coupon.code}</code>
                                  <Badge variant={coupon.active ? "default" : "secondary"}>
                                    {coupon.active ? "Active" : "Inactive"}
                                  </Badge>
                                  <Badge variant="outline">
                                    {coupon.discount_type === "percent_off" 
                                      ? `${coupon.discount_value}% off`
                                      : `$${coupon.discount_value} off`
                                    }
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                                  <span>{coupon.name}</span>
                                  <span>•</span>
                                  <span>{getDurationLabel(coupon.duration)}</span>
                                  <span>•</span>
                                  <span>{coupon.times_redeemed} redeemed</span>
                                  {coupon.max_redemptions && (
                                    <>
                                      <span>•</span>
                                      <span>Max: {coupon.max_redemptions}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {coupon.active ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCouponAction(coupon.id, "deactivate")}
                                  disabled={actionLoading}
                                  data-testid={`deactivate-coupon-${coupon.id}`}
                                >
                                  <ToggleRight className="w-4 h-4 mr-1" />
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCouponAction(coupon.id, "activate")}
                                  disabled={actionLoading}
                                  data-testid={`activate-coupon-${coupon.id}`}
                                >
                                  <ToggleLeft className="w-4 h-4 mr-1" />
                                  Activate
                                </Button>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`delete-coupon-${coupon.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Coupon?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete the coupon <strong>{coupon.code}</strong> from both Tally and Stripe. 
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleCouponAction(coupon.id, "delete")}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Articles / CMS Section */}
            {activeSection === "articles" && (
              <Suspense fallback={<Skeleton className="h-64 rounded-xl" />}>
                <AdminArticlesSection api={api} />
              </Suspense>
            )}

            {/* Affiliates Section */}
            {activeSection === "affiliates" && (
              <Suspense fallback={<Skeleton className="h-64 rounded-xl" />}>
                <AdminAffiliatesSection api={api} />
              </Suspense>
            )}

            {/* Announcements Section */}
            {activeSection === "announcements" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">Announcement Bar</h3>
                    <Button variant="outline" size="icon" onClick={fetchAnnouncements} data-testid="refresh-announcements-btn">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button onClick={() => setShowCreateAnnouncement(true)} data-testid="create-announcement-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Announcement
                  </Button>
                </div>

                {/* Preview of active announcement */}
                {announcements.find(a => a.active) && (
                  <Card className="overflow-hidden">
                    <CardHeader className="py-3 px-4 bg-muted/50">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Live Preview
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      {(() => {
                        const active = announcements.find(a => a.active);
                        return (
                          <div
                            className="w-full text-center py-2.5 px-4 text-sm font-medium"
                            style={{ backgroundColor: active.bg_color || "#1a1a2e", color: active.text_color || "#ffffff" }}
                          >
                            <span>{active.message}</span>
                            {active.link_url && (
                              <span className="ml-2 underline font-semibold">
                                {active.link_text || "Learn more"} →
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}

                {/* Create Announcement Dialog */}
                <Dialog open={showCreateAnnouncement} onOpenChange={setShowCreateAnnouncement}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create Announcement</DialogTitle>
                      <DialogDescription>This will appear at the top of all public pages. Creating a new one deactivates any existing announcement.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Message *</Label>
                        <Input
                          placeholder="e.g. Launch special! Use code SAVE20 for 20% off"
                          value={newAnnouncement.message}
                          onChange={(e) => setNewAnnouncement(p => ({ ...p, message: e.target.value }))}
                          data-testid="announcement-message-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Link URL (optional)</Label>
                          <Input
                            placeholder="https://... or /pricing"
                            value={newAnnouncement.link_url}
                            onChange={(e) => setNewAnnouncement(p => ({ ...p, link_url: e.target.value }))}
                            data-testid="announcement-link-input"
                          />
                        </div>
                        <div>
                          <Label>Link Text (optional)</Label>
                          <Input
                            placeholder="e.g. Sign up now"
                            value={newAnnouncement.link_text}
                            onChange={(e) => setNewAnnouncement(p => ({ ...p, link_text: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Background Color</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={newAnnouncement.bg_color}
                              onChange={(e) => setNewAnnouncement(p => ({ ...p, bg_color: e.target.value }))}
                              className="w-10 h-10 rounded border cursor-pointer"
                            />
                            <Input
                              value={newAnnouncement.bg_color}
                              onChange={(e) => setNewAnnouncement(p => ({ ...p, bg_color: e.target.value }))}
                              className="flex-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Text Color</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={newAnnouncement.text_color}
                              onChange={(e) => setNewAnnouncement(p => ({ ...p, text_color: e.target.value }))}
                              className="w-10 h-10 rounded border cursor-pointer"
                            />
                            <Input
                              value={newAnnouncement.text_color}
                              onChange={(e) => setNewAnnouncement(p => ({ ...p, text_color: e.target.value }))}
                              className="flex-1"
                            />
                          </div>
                        </div>
                      </div>
                      {/* Inline preview */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Preview</Label>
                        <div
                          className="w-full text-center py-2.5 px-4 text-sm font-medium rounded-lg mt-1"
                          style={{ backgroundColor: newAnnouncement.bg_color, color: newAnnouncement.text_color }}
                        >
                          {newAnnouncement.message || "Your announcement preview"}
                          {newAnnouncement.link_url && (
                            <span className="ml-2 underline font-semibold">{newAnnouncement.link_text || "Learn more"} →</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowCreateAnnouncement(false)}>Cancel</Button>
                      <Button onClick={handleCreateAnnouncement} disabled={savingAnnouncement} data-testid="save-announcement-btn">
                        {savingAnnouncement ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Create & Activate
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Announcements List */}
                {announcementsLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 rounded-xl" />
                    <Skeleton className="h-16 rounded-xl" />
                  </div>
                ) : announcements.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p>No announcements yet</p>
                      <p className="text-xs mt-1">Create one to display a banner on all public pages</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {announcements.map((ann) => (
                      <Card key={ann.id} className={ann.active ? "ring-2 ring-primary/50" : "opacity-70"}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={ann.active
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                }>
                                  {ann.active ? "Live" : "Inactive"}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(ann.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="font-medium text-sm truncate">{ann.message}</p>
                              {ann.link_url && (
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                  Link: {ann.link_url} ({ann.link_text || "Learn more"})
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <div className="w-4 h-4 rounded border" style={{ backgroundColor: ann.bg_color }} />
                                <span className="text-xs text-muted-foreground">BG: {ann.bg_color}</span>
                                <div className="w-4 h-4 rounded border" style={{ backgroundColor: ann.text_color }} />
                                <span className="text-xs text-muted-foreground">Text: {ann.text_color}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {ann.active ? (
                                <Button variant="outline" size="sm" onClick={() => handleAnnouncementAction(ann.id, "deactivate")}>
                                  <ToggleRight className="w-4 h-4 mr-1" />
                                  Deactivate
                                </Button>
                              ) : (
                                <Button variant="outline" size="sm" onClick={() => handleAnnouncementAction(ann.id, "activate")}>
                                  <ToggleLeft className="w-4 h-4 mr-1" />
                                  Activate
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleAnnouncementAction(ann.id, "delete")}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Privacy Gateway Section */}
            {activeSection === "privacy" && (
              <Suspense fallback={<Skeleton className="h-64 rounded-xl" />}>
                <AdminPrivacySection api={api} />
              </Suspense>
            )}

            {/* Advisors Section */}
            {activeSection === "advisors" && (
              <Suspense fallback={<Skeleton className="h-64 rounded-xl" />}>
                <AdminAdvisorsSection api={api} />
              </Suspense>
            )}

            {/* Security & Compliance Section */}
            {activeSection === "compliance" && (
              <Suspense fallback={<Skeleton className="h-64 rounded-xl" />}>
                <AdminSecurityCompliance api={api} />
              </Suspense>
            )}

            {/* Security Section */}
            {activeSection === "security" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">Admin Login Attempts</h3>
                    <Button variant="outline" size="icon" onClick={fetchLoginAttempts}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">{attemptsTotal} total attempts</p>
                </div>

                <Card>
                  <CardContent className="p-0">
                    {attemptsLoading ? (
                      <div className="p-6 space-y-3">
                        {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                      </div>
                    ) : loginAttempts.length === 0 ? (
                      <div className="p-12 text-center">
                        <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                        <p className="text-muted-foreground">No login attempts recorded</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {loginAttempts.map((attempt) => {
                          const statusColors = {
                            success: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                            mfa_sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                            failed_password: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                            failed_not_found: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
                            failed_mfa_expired: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
                            failed_mfa_attempts: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                          };
                          const statusLabels = {
                            success: "Success",
                            mfa_sent: "MFA Sent",
                            failed_password: "Wrong Password",
                            failed_not_found: "Not Found",
                            failed_mfa_expired: "MFA Expired",
                            failed_mfa_attempts: "MFA Failed",
                          };
                          return (
                            <div key={attempt.id} className="p-4 flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  attempt.status === "success" ? "bg-green-100 dark:bg-green-900/30" : 
                                  attempt.status === "mfa_sent" ? "bg-blue-100 dark:bg-blue-900/30" :
                                  "bg-red-100 dark:bg-red-900/30"
                                }`}>
                                  {attempt.status === "success" ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  ) : attempt.status === "mfa_sent" ? (
                                    <Mail className="w-5 h-5 text-blue-600" />
                                  ) : (
                                    <XCircle className="w-5 h-5 text-red-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium">{attempt.email}</p>
                                  <p className="text-sm text-muted-foreground">
                                    IP: {attempt.ip_address?.split(",")[0]} • {new Date(attempt.timestamp).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <Badge className={statusColors[attempt.status] || "bg-gray-100 text-gray-700"}>
                                {statusLabels[attempt.status] || attempt.status}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  </div>

  {/* Subscription Management Modal */}
  <Dialog open={subModalOpen} onOpenChange={setSubModalOpen}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {subAction?.type === 'comp' && <Gift className="h-5 w-5 text-purple-500" />}
          {subAction?.type === 'trial' && <CalendarPlus className="h-5 w-5 text-blue-500" />}
          {subAction?.type === 'paid' && <Crown className="h-5 w-5 text-amber-500" />}
          {subAction?.type === 'free' && <XCircle className="h-5 w-5 text-muted-foreground" />}
          {subAction?.type === 'comp' && 'Grant Comp Access'}
          {subAction?.type === 'trial' && 'Extend Trial Period'}
          {subAction?.type === 'paid' && 'Grant Paid Access'}
          {subAction?.type === 'free' && 'Set to Free'}
        </DialogTitle>
        <DialogDescription>
          {subAction?.type === 'comp' && `Grant complimentary Power access to ${subAction?.email}`}
          {subAction?.type === 'trial' && `Extend or grant trial period for ${subAction?.email}`}
          {subAction?.type === 'paid' && `Grant permanent paid Power access to ${subAction?.email}`}
          {subAction?.type === 'free' && `Remove all paid access for ${subAction?.email}`}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {/* Duration Selection - only for comp and trial */}
        {(subAction?.type === 'comp' || subAction?.type === 'trial') && (
          <div className="space-y-2">
            <Label>Duration</Label>
            <Select value={subDuration} onValueChange={setSubDuration}>
              <SelectTrigger>
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1_week">1 Week</SelectItem>
                <SelectItem value="1_month">1 Month</SelectItem>
                <SelectItem value="3_months">3 Months</SelectItem>
                <SelectItem value="6_months">6 Months</SelectItem>
                <SelectItem value="1_year">1 Year</SelectItem>
                {subAction?.type === 'comp' && <SelectItem value="forever">Forever (Lifetime)</SelectItem>}
                <SelectItem value="custom">Custom Days</SelectItem>
              </SelectContent>
            </Select>
            
            {subDuration === 'custom' && (
              <div className="pt-2">
                <Label>Number of Days</Label>
                <Input 
                  type="number" 
                  placeholder="e.g., 45"
                  value={subCustomDays}
                  onChange={(e) => setSubCustomDays(e.target.value)}
                  min="1"
                  max="3650"
                />
              </div>
            )}
          </div>
        )}

        {/* Reason/Note */}
        <div className="space-y-2">
          <Label>Reason / Note (Optional)</Label>
          <Textarea 
            placeholder={
              subAction?.type === 'comp' ? "e.g., Won giveaway, influencer partnership, customer service resolution..." :
              subAction?.type === 'trial' ? "e.g., User requested extension, technical issues during trial..." :
              subAction?.type === 'paid' ? "e.g., Lifetime customer, partner account..." :
              "e.g., User requested downgrade, billing issue..."
            }
            value={subReason}
            onChange={(e) => setSubReason(e.target.value)}
            rows={2}
          />
        </div>

        {/* Info Box */}
        <div className={`p-3 rounded-lg text-sm ${
          subAction?.type === 'comp' ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' :
          subAction?.type === 'trial' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
          subAction?.type === 'paid' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300' :
          'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300'
        }`}>
          {subAction?.type === 'comp' && (
            <p>✨ User will get full Power features. No Stripe billing. Access will expire after the selected duration (unless Forever).</p>
          )}
          {subAction?.type === 'trial' && (
            <p>📅 This will extend or start a trial period. User will be prompted to subscribe when trial ends.</p>
          )}
          {subAction?.type === 'paid' && (
            <p>👑 User will get permanent Power access with no expiration. No Stripe billing will be created.</p>
          )}
          {subAction?.type === 'free' && (
            <p>⚠️ This will cancel any active subscription and remove premium features. User can still use basic features.</p>
          )}
        </div>
      </div>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="outline" onClick={() => setSubModalOpen(false)}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            const options = {
              duration: subDuration !== 'custom' ? subDuration : undefined,
              customDays: subDuration === 'custom' ? subCustomDays : undefined,
              reason: subReason || undefined,
            };
            handleSetSubscriptionStatus(subAction?.userId, subAction?.type === 'trial' ? 'trial_extension' : subAction?.type, options);
          }}
          disabled={actionLoading || (subDuration === 'custom' && !subCustomDays)}
          className={
            subAction?.type === 'comp' ? 'bg-purple-600 hover:bg-purple-700' :
            subAction?.type === 'trial' ? 'bg-blue-600 hover:bg-blue-700' :
            subAction?.type === 'paid' ? 'bg-amber-500 hover:bg-amber-600' :
            'bg-destructive hover:bg-destructive/90'
          }
        >
          {actionLoading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
          ) : (
            <>
              {subAction?.type === 'comp' && 'Grant Comp Access'}
              {subAction?.type === 'trial' && 'Extend Trial'}
              {subAction?.type === 'paid' && 'Grant Paid Access'}
              {subAction?.type === 'free' && 'Set to Free'}
            </>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  </div>
  );
};

export default AdminPage;
